import { useGTMDispatch } from '@elgorditosalsero/react-gtm-hook';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { ActivationScreenContainer } from '@/components/Elements';
import { EUR, PRODUCT_FREE } from '@/config/appConstants';
import { useProducts } from '@/features/products';
import { useMe } from '@/hooks/useMe';
import { useAuth } from '@/lib/auth';
import {
  buildGTMCheckoutData,
  buildGTMPaidPlanData,
  buildGTMValidationAccount,
  sendEventGtm,
} from '@/lib/gtm/gtmUtils';
import { Currency, iAuthInfo, BillingMode, PricesTypes } from '@/types';
import {
  filterHiddenPriceProducts,
  getAppDashboardURL,
  getProductFilterPricesByCurrencyBillingMode,
  getProductLicensedPriceByCurrency,
  isAdminUserFromMe,
  isAppSumo,
  isCurrency,
  isFreemiumProduct,
} from '@/utils/helpers';
import storage from '@/utils/storage';

export const ActivationPage = () => {
  const { t, i18n } = useTranslation();
  const { getAuthInfo } = useAuth();
  const authInfo: iAuthInfo = getAuthInfo();
  const sendDataToGTM = useGTMDispatch();
  const { mdirectorId = PRODUCT_FREE.toLowerCase() } = useParams();
  const { currency, landingname, billingMode } = useParams();
  const { getMe } = useMe();
  const me = getMe();
  const productsQuery = useProducts();
  const { isLoading: productsIsLoading = false, data: products } = productsQuery;

  const redirectAppHome = () => {
    setTimeout(() => {
      //https://payments.dev.mdirector.com/account/activation/pro-20000/year/eur/LP_es_signup_+landing_optimizer
      window.location.assign(getAppDashboardURL());
    }, 7000);
    return renderActivationScreenContainer();
  };

  const renderActivationScreenContainer = () => {
    return (
      <ActivationScreenContainer
        title={t('ActivationScreen.title')}
        description={t('ActivationScreen.description')}
      />
    );
  };

  useEffect(() => {
    if (!productsIsLoading && products) {
      const product = products && filterHiddenPriceProducts(products, mdirectorId);
      if (product && !isFreemiumProduct(product) && !isAppSumo(me)) {
        storage.setPendingProduct(
          getProductFilterPricesByCurrencyBillingMode(
            product,
            (currency as Currency) || EUR,
            billingMode as BillingMode
          )
        );
      }
    }
  }, [productsIsLoading, products, mdirectorId, currency, billingMode, me]);

  if (productsIsLoading) {
    return renderActivationScreenContainer();
  }

  storage.clearUserData();

  if (isCurrency(currency as Currency)) {
    storage.setUserCurrency(currency as Currency);
  }

  if (!Object.keys(authInfo).length || !products) {
    return redirectAppHome();
  }
  const product = products && filterHiddenPriceProducts(products, mdirectorId);

  if (isAdminUserFromMe(me) && !isAppSumo(me)) {
    sendEventGtm(buildGTMValidationAccount(getMe()), sendDataToGTM);
  }

  sendEventGtm(
    buildGTMCheckoutData(product, landingname, currency as Currency, authInfo, i18n.language),
    sendDataToGTM
  );

  const productPrice: PricesTypes = getProductLicensedPriceByCurrency(
    product.prices,
    currency as Currency
  );
  isAppSumo(me) &&
    sendEventGtm(buildGTMPaidPlanData(product, productPrice, i18n.language, me), sendDataToGTM);

  return redirectAppHome();
};
